import { useState } from 'react';
import { api, type TenantSchedule } from '../api.ts';
import { message } from './format.ts';
import type { ScheduleView } from './types.ts';

interface ScheduleCardProps {
  tenantId: string;
  view: ScheduleView;
  onSaved: () => void;
  onError: (message: string) => void;
}

/**
 * This client's cadence and pause.
 *
 * The card validates nothing. A cadence the product will not accept is refused
 * by the server, which is also where the database's own constraint lives, so
 * there is one rule about what a cadence may be rather than two that can
 * disagree. The blank box is not "no cadence": it means this client follows the
 * deployment default, and the placeholder says what that number currently is.
 *
 * A pause is a human decision — an off-boarding, a change freeze, a billing
 * hold — and nothing computed overrides it. It is worth saying out loud that a
 * paused client stops accruing evidence, because the state that hurts is a
 * pause set during a migration and never cleared.
 */
export const ScheduleCard = ({ tenantId, view, onSaved, onError }: ScheduleCardProps) => {
  const stored = view.decision.schedule;
  const [cadence, setCadence] = useState(
    stored.cadenceDays === null ? '' : String(stored.cadenceDays),
  );
  const [paused, setPaused] = useState(stored.paused);
  const [reason, setReason] = useState(stored.pauseReason ?? '');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setStatus(null);
    const trimmed = cadence.trim();
    const next: TenantSchedule = {
      cadenceDays: trimmed === '' ? null : Number(trimmed),
      paused,
      pauseReason: paused && reason.trim() ? reason.trim() : null,
    };

    try {
      await api.putTenantSchedule(tenantId, next);
      setStatus(
        paused
          ? 'Saved. Scheduled assessments are paused for this client until you clear the pause.'
          : 'Saved. The next sweep will use this cadence.',
      );
      onSaved();
    } catch (cause) {
      onError(message(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>Scheduled assessments</h2>
      </div>
      <div className="card-body">
        <div className="callout td-plan">{view.decision.detail}</div>

        <div className="td-fields">
          <div className="field">
            <label htmlFor="cadence">Cadence in days</label>
            <input
              id="cadence"
              type="number"
              value={cadence}
              placeholder={`${view.defaultCadenceDays} — the deployment default`}
              aria-describedby="cadence-help"
              onChange={(event) => setCadence(event.target.value)}
            />
            <span className="hint" id="cadence-help">
              Leave this empty to follow the deployment default of {view.defaultCadenceDays} days.
            </span>
          </div>

          <div className="field">
            <label htmlFor="pause-reason">Pause reason</label>
            <input
              id="pause-reason"
              value={reason}
              disabled={!paused}
              placeholder="tenant migration until March"
              onChange={(event) => setReason(event.target.value)}
            />
            <span className="hint">
              Optional, and only recorded while the pause is on. A paused client stops accruing
              evidence, so the reason is what stops a pause outliving its cause.
            </span>
          </div>
        </div>

        <div className="field td-check">
          <label htmlFor="paused">
            <input
              id="paused"
              type="checkbox"
              checked={paused}
              onChange={(event) => setPaused(event.target.checked)}
            />
            Pause scheduled assessments for this client
          </label>
        </div>

        <div className="toolbar">
          <button
            type="button"
            className="btn btn-primary"
            aria-busy={saving}
            disabled={saving}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save schedule'}
          </button>
          <span className="status-inline" role="status">
            {status}
          </span>
        </div>
      </div>
    </div>
  );
};
